<template>
    <div class="pt-4">
        <div v-if="loading" class="space-y-4">
            <USkeleton class="h-7 w-48" />
            <USkeleton class="h-16" />
            <USkeleton class="h-96" />
        </div>

        <div v-else-if="loadError" class="mt-6 flex flex-col items-center rounded-lg bg-white px-6 py-16 text-center shadow-sm ring-1 ring-black/5">
            <UIcon name="i-ph-warning-duotone" class="size-8 text-red-400" />
            <h2 class="mt-3 text-base font-semibold text-gray-800">Couldn't load your profile</h2>
            <p class="mt-1 text-sm text-gray-500">{{ loadError }}</p>
            <UButton @click="retry()" :loading="retrying" color="white" class="mt-5" icon="i-ph-arrow-clockwise-bold">Retry</UButton>
        </div>

        <div v-else-if="!profile" class="mt-6 flex flex-col items-center rounded-lg bg-white px-6 py-16 text-center shadow-sm ring-1 ring-black/5">
            <UIcon name="i-ph-stethoscope-duotone" class="size-8 text-gray-400" />
            <h2 class="mt-3 text-base font-semibold text-gray-800">No doctor profile found</h2>
            <p class="mt-1 text-sm text-gray-500">Your account is not linked to a doctor profile yet. Contact the telemedicine team.</p>
        </div>

        <template v-else>
            <h1 class="mb-4 text-2xl font-semibold text-gray-700">My profile</h1>

            <UAlert
                v-if="profile.status === DoctorStatus.published"
                color="green"
                variant="subtle"
                icon="i-ph-broadcast-duotone"
                title="Your profile is live in the SAMU app."
                class="mb-4"
            />
            <UAlert
                v-else-if="profile.status === DoctorStatus.suspended"
                color="red"
                variant="subtle"
                icon="i-ph-prohibit-duotone"
                title="Your profile is currently hidden. Contact the telemedicine team."
                class="mb-4"
            />
            <UAlert
                v-else
                color="amber"
                variant="subtle"
                icon="i-ph-eye-slash-duotone"
                title="Your profile is not yet visible to patients. The telemedicine team publishes it once it's complete."
                class="mb-4"
            >
                <template v-if="gaps.doctor.length || gaps.team.length" #description>
                    <ul v-if="gaps.doctor.length" class="mt-1 space-y-0.5">
                        <li v-for="blocker in gaps.doctor" :key="blocker">• {{ blocker }}</li>
                    </ul>
                    <p v-if="gaps.team.length" class="mt-1">Waiting on the telemedicine team: {{ gaps.team.join(', ') }}</p>
                </template>
            </UAlert>

            <div class="max-w-3xl overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                <UForm v-if="state" ref="form" @submit="save" :schema="doctorProfileFormSchema" :state="state" :validate-on="['submit']">
                    <div class="flex items-center justify-between border-b border-gray-300 px-6 py-3">
                        <h3 class="text-lg font-bold text-gray-700">Profile</h3>
                        <span v-if="dirty" class="flex items-center gap-1.5 text-xs font-medium text-amber-600">
                            <span class="size-1.5 rounded-full bg-amber-500" />
                            Unsaved changes
                        </span>
                    </div>
                    <div class="px-6 py-5">
                        <TelemedicineDoctorProfileForm v-model="state" mode="doctor" :specialties="specialties" :profile-id="profile.id" />
                    </div>
                    <div class="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50/60 px-6 py-3">
                        <UButton v-if="dirty" @click="reset()" color="white">Discard</UButton>
                        <UButton type="submit" :loading="saving" :disabled="!dirty" color="black" icon="i-ph-floppy-disk-duotone">
                            Save changes
                        </UButton>
                    </div>
                </UForm>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import { DoctorStatus, type DoctorProfile, type DoctorProfileFields, type Specialty } from '~/types/telemedicine'
import { PUBLISH_INVARIANT_MESSAGE, doctorProfileFormSchema, isPublishInvariantError, normalizeDoctorFields, pickDoctorFields, publishGaps } from '~/utils/telemedicineDoctor'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.telemedicineDoctor] })
useHead({ title: 'Telemedicine – My profile' })

const client = useSupabaseClient()
const auth = useAuthStore()
const toast = useToast()

const EDITABLE = ['full_name', 'title', 'bio_en', 'bio_ar', 'photo_path', 'languages', 'license_number', 'years_experience'] as const

const loading = ref(true)
const saving = ref(false)
const retrying = ref(false)
const loadError = ref<string>()
const profile = ref<DoctorProfile>()
const state = ref<DoctorProfileFields>()
const form = ref()

const specialties = computed<Pick<Specialty, 'id' | 'name_en' | 'active'>[]>(() => (
    profile.value?.specialty ? [{ id: profile.value.specialty.id, name_en: profile.value.specialty.name_en, active: true }] : []
))

const gaps = computed(() => (profile.value ? publishGaps(profile.value) : { doctor: [], team: [] }))

function editable(fields: DoctorProfileFields) {
    const normalized = normalizeDoctorFields(fields)
    return Object.fromEntries(EDITABLE.map((key) => [key, normalized[key]]))
}

const dirty = computed(() => !!profile.value && !!state.value
    && JSON.stringify(editable(state.value)) !== JSON.stringify(editable(pickDoctorFields(profile.value))))

onMounted(async () => {
    try {
        await load()
    } finally {
        loading.value = false
    }
})

async function load() {
    try {
        const { data, error } = await client.from('doctor_profiles')
            .select('*, specialty:specialties(id, name_en, name_ar)')
            .eq('user_id', auth.user!.id)
            .maybeSingle()
        if (error) throw error
        loadError.value = undefined
        profile.value = (data ?? undefined) as unknown as DoctorProfile | undefined
        if (profile.value) state.value = pickDoctorFields(profile.value)
    } catch (e: any) {
        if (!profile.value) loadError.value = e?.message || 'Something went wrong. Check your connection and try again.'
        toast.add({ title: 'Could not load your profile', description: e?.message, icon: 'i-ph-warning-duotone', color: 'red' })
    }
}

async function retry() {
    retrying.value = true
    try {
        await load()
    } finally {
        retrying.value = false
    }
}

function saveErrorMessage(error: { code?: string, message: string } | null) {
    if (!error) return 'Your profile was not updated. Reload the page and try again.'
    if (isPublishInvariantError(error)) return PUBLISH_INVARIANT_MESSAGE
    if (error.code === '42501') return 'Some fields are managed by the telemedicine team.'
    return error.message
}

function reset() {
    if (profile.value) state.value = pickDoctorFields(profile.value)
    form.value?.clear()
}

async function save() {
    if (!profile.value || !state.value) return
    saving.value = true

    const { data, error } = await client.from('doctor_profiles')
        .update(editable(state.value) as never)
        .eq('id', profile.value.id)
        .select('id')

    saving.value = false

    if (error || !data?.length) {
        toast.add({
            title: 'Could not save your profile',
            description: saveErrorMessage(error),
            icon: 'i-ph-warning-duotone',
            color: 'red',
        })
        return
    }

    toast.add({ title: 'Profile saved', icon: 'i-ph-check-circle-duotone', color: 'green' })
    await load()
}
</script>
